/**
 * Pure mapping from relay events onto the voice session's outbound contract.
 *
 * `voiceSession.js` owns a microphone, an AudioContext, a shared socket and
 * five timers, none of which can be constructed in a plain node process. That
 * is precisely why the part most likely to be wrong — which relay event becomes
 * which `onEvent` emission, in what order, and what bookkeeping it moves —
 * lives here instead: no browser globals, no timers, no I/O, nothing to mock.
 * `scripts/test-voice-relay-events.js` runs this module directly.
 *
 * The shape is deliberately data, not callbacks. `mapRelayEvent` returns three
 * lists and the session performs them:
 *
 *   emits   [{ type, payload }]   passed straight to `onEvent(type, payload)`
 *   patch   { field: value }      merged onto the session's bookkeeping
 *   actions [{ kind, ... }]       timer/transport work only the session can do
 *
 * Returning the emissions rather than calling them is what lets a test assert
 * ORDER and SUPPRESSION, which is where the interesting bugs are: a spoken
 * "confirm" during an open confirm window must produce a confirmation and must
 * NOT also produce a command proposal, because the relay parses every final
 * transcript and does not know a confirm window exists.
 */

// Whole-utterance confirmation vocabulary. Matching is exact against the
// normalized string: "confirm the move please" is not a confirmation, because a
// substring match on a destructive path is how an accidental apply happens.
const STANDARD_CONFIRM_PHRASES = new Set([
  "confirm",
  "yes",
  "yes confirm",
  "confirmed",
  "apply",
  "apply it",
  "do it",
]);
const DELETE_CONFIRM_PHRASES = new Set([
  "confirm delete",
  "confirm deletion",
  "delete confirmed",
  "confirm the delete",
]);
const CANCEL_PHRASES = new Set([
  "cancel",
  "no",
  "stop",
  "never mind",
  "nevermind",
  "cancel it",
]);

export function normalizeVoiceConfirmation(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Classify one complete, already-normalized utterance for a pending intent. */
export function classifyVoiceConfirmation(normalized, intent) {
  if (CANCEL_PHRASES.has(normalized)) return "cancel";
  if (intent === "delete_box") {
    if (DELETE_CONFIRM_PHRASES.has(normalized)) return "confirm";
    if (STANDARD_CONFIRM_PHRASES.has(normalized)) return "wrong-phrase";
    return null;
  }
  return STANDARD_CONFIRM_PHRASES.has(normalized) ? "confirm" : null;
}

/**
 * Relay stop reasons onto the reason strings `VoiceCommandModal.onSessionClosed`
 * already understands. The modal is not being changed, so this is a translation
 * layer and not a new vocabulary.
 *
 * `no_audio` and `max_session` are the server-side versions of the two client
 * timers that used to end a session on their own, so they carry those timers'
 * reasons and therefore their copy: a session that ran out of time is
 * "stopped", not "the connection was lost".
 */
export const RELAY_STOP_REASONS = Object.freeze({
  no_audio: "idle",
  max_session: "timeout",
  client_stop: "user",
  client_end: "user",
  muse_closed: "transport-lost",
  backlog: "transport-lost",
});

/**
 * `error` is the one reason that depends on what already reached the screen.
 *
 * The relay always sends `voice:error` before `voice:stopped {reason:'error'}`,
 * and that error has already been rendered as a refusal banner on the surface
 * the user is looking at. Reporting `transport-lost` on top of it would fire a
 * danger toast AND close the modal, taking the explanation away at the exact
 * moment it became useful. So a reported error closes the session the same way
 * a timer does, leaving the banner readable. An `error` stop with no preceding
 * `voice:error` explained nothing, and does get `transport-lost`.
 */
export function mapStopReason(reason, state) {
  if (reason === "error") {
    return state && state.sawError ? "idle" : "transport-lost";
  }
  return RELAY_STOP_REASONS[reason] || "transport-lost";
}

/**
 * Fallback copy per error code. The server's own `message` wins whenever it
 * sends one; this exists so a code with no message is still a sentence rather
 * than an identifier.
 */
export const RELAY_ERROR_COPY = Object.freeze({
  VOICE_ALLOWANCE_EXHAUSTED:
    "This lab's voice minutes for the month are used up.",
  VOICE_BETA_DISABLED: "Voice is not enabled for this lab.",
  VOICE_ALREADY_STREAMING: "A voice session is already running.",
  VOICE_BUSY: "Too many voice sessions are running in this lab right now.",
  VOICE_STT_UNCONFIGURED: "The voice service is not configured.",
  VOICE_STT_ERROR: "The voice service could not handle that request.",
  VOICE_BACKLOG: "Audio arrived faster than it could be sent.",
  VOICE_SOCKET_TIMEOUT: "Could not reach the voice service.",
});

export function relayErrorMessage(code, message) {
  if (message) return String(message);
  return (
    RELAY_ERROR_COPY[code] || "The voice service could not handle that request."
  );
}

/** An Error the modal can render: it reads `.message`, diagnostics read `.code`. */
export function relayError(code, message) {
  return Object.assign(new Error(relayErrorMessage(code, message)), {
    code: code || null,
  });
}

/**
 * What a parsed tool call actually is, before any emitting happens.
 *
 * Lifted verbatim out of `raiseProposal` so the two branches that matter can be
 * tested without a session: the `preview_` strip (the relay mints wire names
 * under that prefix, command intents do not carry it) and
 * `request_inventory_clarification`, which is a question for the user and must
 * never reach the command endpoints.
 */
export function classifyToolCall(toolName, rawArguments) {
  if (!toolName) return { kind: "ignored", intent: null, fields: null };

  let fields;
  try {
    fields = JSON.parse(rawArguments || "{}");
  } catch {
    fields = null;
  }
  // Deliberately the same falsy test the xAI path used: `null`, `0` and `""`
  // are all "the arguments did not survive", and widening it here would change
  // behaviour that nothing has asked to change.
  if (!fields) return { kind: "unparseable", intent: null, fields: null };

  const intent = String(toolName).replace(/^preview_/, "");
  if (intent === "request_inventory_clarification") {
    return { kind: "clarification", intent, fields };
  }
  return { kind: "proposal", intent, fields };
}

/**
 * Stable key for "the turn this event belongs to".
 *
 * A confirmation is suppressed per TURN, not per session: the relay parses
 * every final transcript, so the `voice:command` that follows a spoken
 * "confirm" has to be dropped while the NEXT turn's command must not be. When
 * the relay omits `turnId` both sides fall back to the same sentinel, which
 * degrades to "suppress the next command" rather than to "suppress nothing".
 */
export function relayTurnKey(payload) {
  const turnId = payload && payload.turnId;
  return turnId === undefined || turnId === null ? "__unkeyed__" : String(turnId);
}

const NO_OP = Object.freeze({
  emits: Object.freeze([]),
  patch: Object.freeze({}),
  actions: Object.freeze([]),
});

/**
 * Map one relay event onto emissions, state changes and session work.
 *
 * @param {string} type   the relay event name, e.g. "voice:final"
 * @param {object} payload the relay event body
 * @param {object} state  the session's bookkeeping. Read here:
 *   `confirmIntent`, `confirmWindowStarted`, `wrongConfirmPhraseEmitted`,
 *   `confirmationTurnKey`, `sawError`.
 * @returns {{emits: Array, patch: object, actions: Array}}
 */
export function mapRelayEvent(type, payload, state) {
  const body = payload || {};
  const bookkeeping = state || {};

  switch (type) {
    // The relay has a ledger row and an upstream socket. Audio held while all
    // of that happened is released by the session on this event.
    case "voice:ready":
      return {
        emits: [{ type: "listening" }],
        patch: { relayReady: true },
        actions: [{ kind: "ready", voiceSessionId: body.voiceSessionId || null }],
      };

    // Cumulative: each partial carries the whole utterance so far and REPLACES
    // the last one. Both bounded caps are cleared because the user is audibly
    // mid-sentence, which is the thing those caps exist to detect the absence of.
    case "voice:partial":
      return {
        emits: [{ type: "partialTranscript", payload: body.text || "" }],
        patch: { sawTranscript: true },
        actions: [{ kind: "clearAnswerWindow" }, { kind: "clearNoSpeechCap" }],
      };

    case "voice:final":
      return mapFinal(body, bookkeeping);

    case "voice:command":
      return mapCommand(body, bookkeeping);

    case "voice:error":
      return {
        emits: [
          {
            type: "model-error",
            payload: relayError(body.code, body.message),
          },
        ],
        patch: { sawError: true, awaitingResponse: false },
        actions: [],
      };

    case "voice:stopped":
      return {
        emits: [],
        patch: {},
        actions: [
          {
            kind: "stop",
            reason: mapStopReason(body.reason, bookkeeping),
            seconds: Number.isFinite(body.seconds) ? body.seconds : null,
          },
        ],
      };

    // Read-back audio. Data for the session's chunk assembler, never an emit:
    // what reaches the modal is the `speak` lifecycle the session raises once
    // playback actually starts, ends, or turns out to be unavailable.
    case "voice:speak":
      return {
        emits: [],
        patch: {},
        actions: [{ kind: "speak", payload: body }],
      };

    default:
      return NO_OP;
  }
}

/**
 * A completed turn. The transcript is shown, then — only inside an open confirm
 * window — it is tested as a spoken decision.
 *
 * `confirmIntent && confirmWindowStarted` is both halves on purpose:
 * `prepareConfirmation` installs the current card's intent BEFORE browser
 * speech, but the decision window does not open until playback ends, and a
 * late final from the superseded turn must not confirm the new card in that gap.
 */
function mapFinal(body, state) {
  const text = body.text || "";
  const emits = [{ type: "transcript", payload: text }];
  const patch = {
    sawTranscript: true,
    awaitingResponse: true,
    quietSince: null,
    confirmationTurnKey: null,
    // A fresh turn owns its own record of what it produced.
    turnHadCall: false,
  };
  const actions = [
    { kind: "clearAnswerWindow" },
    { kind: "clearNoSpeechCap" },
    { kind: "armSilenceTimer" },
  ];

  if (!state.confirmIntent || !state.confirmWindowStarted) {
    emits.push({ type: "thinking" });
    return { emits, patch, actions };
  }

  const normalized = normalizeVoiceConfirmation(text);
  const action = classifyVoiceConfirmation(normalized, state.confirmIntent);

  if (!action) {
    // Speech during an open window that is not a decision. It is an ordinary
    // command again, so the relay's parse of it is allowed through.
    emits.push({ type: "thinking" });
    return { emits, patch, actions };
  }

  // Whatever the verdict, this utterance was a DECISION and the relay's parse
  // of it must not also become a proposal — including "confirm" spoken at a
  // delete card, which is a wrong-phrase rather than a command.
  patch.confirmationTurnKey = relayTurnKey(body);

  if (action === "wrong-phrase") {
    // Said once. Repeating "say confirm delete" at every retry is nagging.
    if (state.wrongConfirmPhraseEmitted) return { emits, patch, actions };
    patch.wrongConfirmPhraseEmitted = true;
  }

  // The relay's confirmation epoch (FIX-F9): which turn and which card this word
  // belongs to. Null until the relay sends them; the dock accepts an unhashed
  // confirmation as before and ignores one whose hash is not the card on screen.
  emits.push({
    type: "voiceConfirmation",
    payload: {
      action,
      normalized,
      turnId: body.turnId == null ? null : String(body.turnId),
      previewHash: body.previewHash == null ? null : String(body.previewHash),
    },
  });
  return { emits, patch, actions };
}

/**
 * The relay's parse of a final transcript. This is the only path to a proposal,
 * and a proposal is still only a proposal: `raiseProposal` raises an event and
 * the modal's explicit confirmation is the sole route to apply.
 *
 * Two measurements ride along with the call. `latencyMs` is how long the parse
 * took, and it was already on the wire and already read by the dock — it simply
 * had nothing to travel in, so "Parsed" read an em dash on every turn of every
 * session. `usd` is what that parse cost: 0 for a grammar hit and 0 for the
 * Codex subscription, a real number for a metered model. Both are optional,
 * because the relay that sends them is a separate deployment: a relay that has
 * not been updated sends neither, and a missing measurement must read as "not
 * measured" (null) rather than as a claim of nought, while a missing price is
 * genuinely nothing to add up.
 */
function mapCommand(body, state) {
  if (
    state.confirmationTurnKey &&
    state.confirmationTurnKey === relayTurnKey(body)
  ) {
    // The turn this parse came from was a spoken confirm/cancel. Drop it.
    return { emits: [], patch: { confirmationTurnKey: null }, actions: [] };
  }

  return {
    emits: [],
    patch: {
      turnHadCall: true,
      awaitingResponse: false,
      quietSince: null,
      confirmationTurnKey: null,
    },
    actions: [
      { kind: "clearAnswerWindow" },
      { kind: "armSilenceTimer" },
      {
        kind: "raiseProposal",
        callId: String(body.turnId == null ? "" : body.turnId),
        name: body.tool || null,
        argsJson: JSON.stringify(body.args || {}),
        latencyMs: Number.isFinite(body.latencyMs) ? body.latencyMs : null,
        // "tap" makes the card tap-only (shared-terminal deletes, FIX-F9); anything
        // else, including absent, is the spoken window as today.
        confirmMode: body.confirmMode === "tap" ? "tap" : null,
        // Clamped at zero rather than passed through: a negative price would
        // subtract from the session's running total and understate it, and
        // understating is the one direction the footer must not go.
        usd: Number.isFinite(body.usd) && body.usd > 0 ? body.usd : 0,
      },
    ],
  };
}
